import { useOutletContext } from "react-router-dom";
import { ExternalLink, LogOut, BookOpen, FileText, Mail, Phone } from "lucide-react";
import { signOut } from "@/lib/atlas-api";
import { Card, Skeleton, GhostButton } from "@/components/portal/PortalPrimitives";

function Row({ label, value, mono }) {
  return (
    <>
      <div className="text-[12px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={`text-[14px] text-[#0A1128] ${mono ? "font-mono" : ""}`}>{value ?? <span className="text-slate-400">—</span>}</div>
    </>
  );
}

function fmtDate(s) { if (!s) return null; try { return new Date(s).toLocaleString(); } catch { return s; } }

export default function PortalSettings() {
  const { me } = useOutletContext();

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Settings</p>
        <h1 className="font-display text-[28px] text-[#0A1128] mt-1.5">Account & profile</h1>
      </div>

      {!me ? (
        <Card><Skeleton className="h-4 w-1/2" /><Skeleton className="mt-2 h-4 w-2/3" /></Card>
      ) : (
        <Card testId="settings-profile">
          <div className="grid sm:grid-cols-[160px_1fr] gap-y-3 gap-x-6 items-baseline">
            <Row label="Partner id" value={me.id} mono />
            <Row label="Name" value={me.name} />
            <Row label="Company" value={me.company} />
            <Row label="Email" value={me.email} />
            <Row label="Tier" value={me.tier} />
            <Row label="Status" value={me.active ? "Active" : "Inactive"} />
            <Row label="Created" value={fmtDate(me.created_at)} />
            <Row label="Activated" value={fmtDate(me.activated_at)} />
            <Row label="Expected volume" value={me.expected_volume} />
            <Row label="Signup source" value={me.signup_source} />
            <Row label="Usage alert email" value={me.usage_alert_email} />
            <Row label="Stripe customer" value={me.stripe_customer_id} mono />
            <Row label="Payment method" value={me.stripe_payment_method_attached ? "Attached" : "Not attached"} />
          </div>
        </Card>
      )}

      <Card>
        <p className="font-display text-[16px] text-[#0A1128]">Resources</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <a
            href="https://atlasforgefinancial.com/api/v1/integration-guide"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="settings-link-guide"
            className="rounded-[10px] border border-slate-200 px-4 py-3 hover:border-[#1A4232]/40 transition-colors flex items-start gap-3"
          >
            <BookOpen className="h-4 w-4 text-[#1A4232] mt-0.5" />
            <div>
              <p className="font-medium text-[13.5px] text-[#0A1128] flex items-center gap-1.5">Integration Guide <ExternalLink className="h-3 w-3 opacity-60" /></p>
              <p className="text-[12px] text-slate-500 mt-0.5">Everything to integrate in under an hour.</p>
            </div>
          </a>
          <a
            href="https://atlasforgefinancial.com/api/docs"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="settings-link-swagger"
            className="rounded-[10px] border border-slate-200 px-4 py-3 hover:border-[#1A4232]/40 transition-colors flex items-start gap-3"
          >
            <FileText className="h-4 w-4 text-[#1A4232] mt-0.5" />
            <div>
              <p className="font-medium text-[13.5px] text-[#0A1128] flex items-center gap-1.5">Swagger / OpenAPI <ExternalLink className="h-3 w-3 opacity-60" /></p>
              <p className="text-[12px] text-slate-500 mt-0.5">Interactive API explorer.</p>
            </div>
          </a>
        </div>
      </Card>

      <Card>
        <p className="font-display text-[16px] text-[#0A1128]">Need help?</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-[13px]">
          <a href="mailto:customersupport@atlasforgefinancial.com" className="rounded-[10px] border border-slate-200 px-4 py-3 hover:border-[#1A4232]/40 transition-colors flex items-center gap-3">
            <Mail className="h-4 w-4 text-[#1A4232]" />
            customersupport@atlasforgefinancial.com
          </a>
          <a href="tel:+18432912556" className="rounded-[10px] border border-slate-200 px-4 py-3 hover:border-[#1A4232]/40 transition-colors flex items-center gap-3">
            <Phone className="h-4 w-4 text-[#1A4232]" />
            (843) 291-2556
          </a>
        </div>
      </Card>

      <div className="pt-2">
        <GhostButton testId="settings-sign-out" onClick={signOut}>
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </GhostButton>
      </div>
    </div>
  );
}
